import cookie from 'js-cookie'
import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import {
  constants, sentry, fbq, links, formatPrice, snowplow, getAbValue, getQueueProductsCount,
} from 'common/helpers'
import { getAllQueueProductsArray } from 'common/helpers/getters'
import { getFeatureToggle, getCookieValue } from 'helpers'

import utils from './utils'
import api, { modifyItems } from './api'


/**
 * serverIndex is unique ACTIVE product key
 *
 * @param {Number} serverIndex - must be > -1
 * @param {Array} [months] - queue.items
 * @returns {Object} item
* */
const getItemByServerIndex = (serverIndex, months) => {
  const { models: { queue: { items } } } = getState()

  let item
  const itemsArray = months || items

  itemsArray.some(({ products }) => {
    item = products.find(({ index }) => serverIndex === index)

    return item
  })

  return item
}

/**
 * Get product by iteration index of all products
 *
 * @param {Number} index - iteration index in queue
 * @param {Array} [months] - queue.items
 * @returns {Object} product
* */
const getItemByIndex = (index, months) => {
  const { models: { queue: { items } } } = getState()

  const itemsArray = months || items
  const products = getAllQueueProductsArray(itemsArray)

  return products[index]
}

const fetch = ({ limit } = {}) => {
  reducers.models.queue.setFetching(true)

  return api.fetch({ limit })
    .then(({ items, recommendationTag }) => {
      if (recommendationTag) {
        actions.models.recommendations.update({ tag: recommendationTag, placement: 'queue' })
      }

      reducers.models.queue.setItems(items)
    })
}

const _trackItemAdding = (item) => {
  const { id, name, brand, volume, volumeUnit, productGroup, price } = item

  fbq.trackCustom('AddToQueue', {
    content_type: 'product',
    content_id: id,
    content_name: `${name} by ${brand} ${volume} ${volumeUnit}`,
    content_category: productGroup,
    value: price && `${price}`,
    currency: 'USD',
  })
}

const getLastAddedProduct = (months) => {
  const products = getAllQueueProductsArray(months, true)

  return products[products.length - 1] || {}
}

const resetAddToQueueModals = () => {
  actions.models.ui.resetClosedPopup(constants.modals.addToQueue.ProductAddedToQueue)
  actions.models.ui.resetClosedPopup(constants.modals.addToQueue.Offer)
}

const _addItem = ({ item, metadata, withoutErrorHandler, force, registrationDate }) => {
  const {
    models: { subscription, modals, ui },
    view: { postponedPremiumProduct },
    routing: { location: { pathname } },
  } = getState()

  const {
    closedPopups: {
      [constants.modals.premiumProducts.AddedProduct]: isPremiumProductModalHidden,
      [constants.modals.addToQueue.Offer]: offerModalData,
      [constants.modals.addToQueue.ProductAddedToQueue]: isProductAddedToQueueShown,
    },
  } = ui
  const { isUpgradable, isNotSubscribed, isActive, isPending, currPlan } = subscription
  const { name: planName, shippingPeriod } = currPlan

  const { id, isLimitedEdition, upchargePrice } = item

  const isWeMissedYouModalOpened                = Boolean(modals[constants.modals.WeMissedYou])
  const isSubscriptionUndeliverableModalOpened  = Boolean(modals[constants.modals.SubscriptionUndeliverable])
  const isQueuePopupEnabled                     = getFeatureToggle(constants.features.queuePopup)
  const isQueueUpgradePopupEnabled              = getFeatureToggle(constants.features.queueUpgradePopup)
  const isAddToQueueModalsFlowEnabled           = getAbValue(constants.abTests.addToQueueModals) === 'b'
  const isAddToQueueWidgetEnabled               = getAbValue(constants.abTests.addToQueueWidget) === 'b'


  _trackItemAdding(item)
  // TODO weird code placement to hide CountdownBanner...
  reducers.models.ui.setClosedPopup({ name: 'countdownBanner', value: true })
  reducers.models.me.increaseStatsQueueCount()

  // TODO this is ductape debugger to check why there is "undefined" placement in mixpanel
  if (!metadata || !metadata.placement) {
    const data = JSON.stringify({ item, metadata, withoutErrorHandler, force })
    sentry.captureException(`Placement "undefined": ${data}`)
  }

  if (upchargePrice) {
    metadata.premium = formatPrice(upchargePrice, true, false, 0)
  }

  return request.post('rest/subscription/queue', {
    body: {
      item: id,
      metadata,
      force,
    },
    withErrorModal: false,
    withLoader: true,
    modifyResult: modifyItems,
  })
    .then(({ items: months }) => {
      reducers.models.queue.setItems(months)

      // Block all modals if we have postponed premium product flow
      // We postponed item until payment data have been added
      if (postponedPremiumProduct.product) {
        actions.view.postponedPremiumProduct.reset()

        return months
      }

      // get 2nd month bcs 1st month may be locked with not actual plan size
      const planSize = months[1].status.length
      const {
        count: productsCount,
        activeCount: allActiveProductsCount,
      } = getQueueProductsCount(months)

      const lastTimeAddToQueue        = getCookieValue(constants.cookieNames.lastTimeAddToQueue)
      const registrationDateTimestamp = (new Date(registrationDate)).getTime()

      const isJustRegistered    = (Date.now() - registrationDateTimestamp) < 24 * 60 * 60 * 1000
      const timeAfterAddToQueue = Date.now() - lastTimeAddToQueue

      const isProductAdded2HoursAgo  = Boolean(lastTimeAddToQueue) && timeAfterAddToQueue > 2 * 60 * 60 * 1000
      const isProductAdded24HoursAgo = Boolean(lastTimeAddToQueue) && timeAfterAddToQueue > 24 * 60 * 60 * 1000

      const isPaymentPage = [ links.subscribe, links.checkout ].some((link) => link.startsWith(pathname))

      /*
        priorities of modals from top to bottom
         - add to queue modals flow from https://scentbird.atlassian.net/browse/SCNT-5276
         - old add to queue modal from https://scentbird.atlassian.net/browse/SCNT-5189
         - product with upcharge https://scentbird.atlassian.net/browse/SCNT-4928
         - LEP warning when put it not in first cell https://scentbird.atlassian.net/browse/SCNT-3764
         - upgrade subscription modal

         discussed with @olya
       */

      const isAddToQueueModalsVisible = (
        isAddToQueueModalsFlowEnabled
        && isNotSubscribed
        && !isPaymentPage
        && (
          productsCount === 1
          || productsCount % 5 === 0
        )
      )

      const isOldAddToQueueModalsVisible = (
        !isAddToQueueModalsFlowEnabled
        && isQueuePopupEnabled
        && isNotSubscribed
        && !isPaymentPage
        && (
          productsCount === 1
          || productsCount % 5 === 0
          || isProductAdded24HoursAgo
          || (isJustRegistered && isProductAdded2HoursAgo)
        )
      )

      const isLEPPutFirstInQueueModalVisible = (
        isLimitedEdition
        && allActiveProductsCount > planSize
        && (isActive || isPending)
        && (!months[0].disabled || !months[0].skipped && !months[1].disabled)
        && shippingPeriod === 1
      )

      const isQueueUpgradeSubscriptionModalVisible = (
        isUpgradable
        && planName === constants.planNames.monthly[1]
        && isQueueUpgradePopupEnabled
        && productsCount % 6 === 0
      )

      const isAddToQueueWidgetVisible = (
        isAddToQueueWidgetEnabled
        && isNotSubscribed
        && !isPaymentPage
      )

      const lastAddedProduct = getLastAddedProduct(months)

      if (isAddToQueueWidgetVisible) {
        actions.view.queueWidget.open()
      }
      else if (isAddToQueueModalsVisible) {
        // https://scentbird.atlassian.net/browse/SCNT-5276

        let modalToDisplay = isProductAddedToQueueShown
          ? constants.modals.addToQueue.Offer
          : constants.modals.addToQueue.ProductAddedToQueue

        let offerName = constants.offerNames.secondMonthFree

        if (isJustRegistered && isProductAdded2HoursAgo || isProductAdded24HoursAgo) {
          modalToDisplay = constants.modals.addToQueue.ProductAddedToQueue
          resetAddToQueueModals()
        }
        else if (offerModalData) {
          const { count } = offerModalData

          if (count === 1) {
            offerName = constants.offerNames.off50
          }
          else {
            offerName = constants.offerNames.off25
          }
        }

        actions.models.modals.open(modalToDisplay, { product: item, offerName })
      }
      else if (isOldAddToQueueModalsVisible) {
        actions.models.modals.open(constants.modals.CompleteSubscription, { productsCount })
      }
      else if (upchargePrice && !isPremiumProductModalHidden) {
        actions.models.modals.open(constants.modals.premiumProducts.AddedProduct, {
          isFreeMonth: lastAddedProduct.isFree,
          item: lastAddedProduct,
          onClose: (itemWasRemoved = false) => {
            // if user didn't remove product and if limited product is added not in the first month we show popup for active users
            if (!itemWasRemoved && isLEPPutFirstInQueueModalVisible) {
              actions.models.modals.open(
                constants.modals.limitedEditionProducts.PutFirstInQueue,
                { productIndex: lastAddedProduct.index, item: lastAddedProduct },
              )
            }
          },
        })
      }
      // if limited product is added not in the first month we show popup for active users
      else if (isLEPPutFirstInQueueModalVisible) {
        actions.models.modals.open(
          constants.modals.limitedEditionProducts.PutFirstInQueue,
          { productIndex: lastAddedProduct.index, item: lastAddedProduct },
        )
      }
      else if (isQueueUpgradeSubscriptionModalVisible) {
        actions.models.modals.open(constants.modals.QueueUpgrade)
      }

      // if it's not a premium product or premium product modal was hidden by user ("don't show me this message again")
      if (!upchargePrice || isPremiumProductModalHidden) {
        const lastAddedProductMonth = months.find(({ products }) => products.some(({ index }) => index === lastAddedProduct.index))
        const { month, year } = lastAddedProductMonth
        // TODO replace img to image in products item
        const { name, brand, img, image, url, upchargePrice } = item

        const notificationItem = {
          name,
          brand,
          img,
          image,
          url,
          upchargePrice,
          month,
          year,
        }

        actions.misc.notifications.show('perfumeItem', {
          item: notificationItem,
          isQueueItem: true,
          placement: metadata.placement,
        })
      }

      actions.misc.cookies.setItem(constants.cookieNames.lastTimeAddToQueue, Date.now())

      return months
    }, ({ error, message, resData: { isSubscribedToStockNotification } }) => {
      // we don't display errors in case of re-add limited product after subscription
      if (!withoutErrorHandler) {
        const limitedEditionErrorModal = ({
          lep_need_change_frequency: constants.modals.limitedEditionProducts.NeedChangeFrequency,
          lep_need_resubscribe: constants.modals.limitedEditionProducts.NeedResubscribe,
          lep_need_subscribe: constants.modals.limitedEditionProducts.NeedSubscribe,
          lep_need_unpause: constants.modals.limitedEditionProducts.NeedUnpause,
          lep_need_unskip: constants.modals.limitedEditionProducts.NeedUnskip,
        })[error]

        if (error === 'upcharge_need_payment_data') {
          actions.models.modals.open(constants.modals.premiumProducts.NeedPaymentData, { item })
        }
        else if (error === 'upcharge_need_card_update') {
          actions.models.modals.open(constants.modals.premiumProducts.NeedCardUpdate, { item })
        }
        else if (error === 'upcharge_lep_need_subscribe') {
          actions.models.modals.open(constants.modals.premiumProducts.NeedSubscription, {
            item,
            isNotSubscribed,
            link: isNotSubscribed ? links.subscribe : links.resubscribe,
          })
        }
        else if (error === 'same_perfume_added') {
          const snowplowMetadata = {
            productId: item.id,
            productName: item.name,
            productBrand: item.brand,
            productCategory: item.category,
          }

          actions.models.modals.open(constants.modals.SameFragranceError)
          snowplow.track(constants.snowplowEvents.sameProductPopup, snowplowMetadata, false)
        }
        else if (limitedEditionErrorModal) {
          // Save to add limited edition product after user action
          _addItemPostponed(item, metadata)

          actions.models.modals.open(limitedEditionErrorModal, {
            product: { item, metadata },
            onCancel: removePostponedLimitedItem,
          })
        }
        else if (error === 'lep_need_card_update' && !isWeMissedYouModalOpened) {
          actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateCard)
        }
        else if (error === 'lep_need_address_update' && !isSubscriptionUndeliverableModalOpened) {
          actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateAddress)
        }
        else if (error === 'lep_out_of_stock') {
          actions.models.modals.open(constants.modals.limitedEditionProducts.OutOfStockNotify, {
            productId: id,
            isSubscribedToStockNotification,
          })
        }
        else if (message) {
          actions.models.modals.open(constants.modals.ServerError, {
            content: message,
          })
        }
      }
    })
}

/**
 *
 * @param {Array} [items]
 * @param {Object} [metadata]
 * @param {Object} [options]
 * @param {Boolean} [options.isNotificationVisible] - show notification after action
 * @param {Boolean} [options.withoutErrorHandler] - ignore errors
 * @param {Boolean} [options.postponeLimitedItems] - postpone limited items with error for future request
 * @returns {Promise.<any>}
 * @private
 */
const _addItems = (items, metadata, options = {}) => {
  const { isNotificationVisible, withoutErrorHandler, postponeLimitedItems } = options
  const { models: { modals } } = getState()
  const isWeMissedYouModalOpened                = Boolean(modals[constants.modals.WeMissedYou])
  const isSubscriptionUndeliverableModalOpened  = Boolean(modals[constants.modals.SubscriptionUndeliverable])

  const filteredItems = items
    ? items.filter((item) => item)
    : []

  if (!filteredItems.length) {
    sentry.captureException(`_addItems with empty array`, {
      extra: {
        items,
        metadata,
        options,
      },
    })

    return Promise.resolve()
  }

  fbq.trackCustom('AddToQueue')
  reducers.models.me.increaseStatsQueueCount()

  return request.post('rest/subscription/queue', {
    body: {
      items: filteredItems,
      metadata,
    },
    withErrorModal: false,
  })
    .then(
      (result) => {
        const { items: months } = modifyItems(result)
        const { errors } = result

        const { models: { queue: { items: oldMonths } } } = getState()

        const { activeCount: countBefore } = getQueueProductsCount(oldMonths)
        const { activeCount: countAfter } = getQueueProductsCount(months)

        const countOfAdded = countAfter - countBefore

        reducers.models.queue.setItems(months)

        if (isNotificationVisible && countOfAdded > 0) {
          // TODO there is bug bcs items is array if id ('perfumeItem' expects object)
          // if (items.length === 1) {
          //   actions.misc.notifications.show('perfumeItem', { item: items[0], isQueueItem: true })
          // }
          // else {
          //   actions.misc.notifications.show('severalQueueItems', { itemsCount: items.length })
          // }
          actions.misc.notifications.show('severalQueueItems', { itemsCount: countOfAdded })
        }

        // if it's a first attempt to add items and we got LEP errors
        // we should try to add LEP items later
        if (errors && errors.length) {

          // Filter LEP items by error for adding after subscription
          const lepErrors = [
            'lep_need_change_frequency',
            'lep_need_resubscribe',
            'lep_need_subscribe',
            'lep_need_unpause',
            'lep_need_unskip',
            'lep_need_card_update',
            'lep_need_address_update',
            'upcharge_lep_need_subscribe',
          ]
          let firstError = null
          const failedItems = errors
            .filter(({ error }) => lepErrors.includes(error))
            .map(({ id, error }) => {
              if (!firstError) {
                firstError = error
              }

              return id
            })

          if (failedItems.length) {

            // If it's a postpone request we should postpone items for future and don't through an error
            if (postponeLimitedItems) {
              // Save items to add after registration / authorization
              _addLimitedItemsPostponed(failedItems, metadata, { isNotificationVisible })
            }
            else if (!withoutErrorHandler) {
              const limitedEditionErrorModal = ({
                lep_need_change_frequency: constants.modals.limitedEditionProducts.NeedChangeFrequency,
                lep_need_resubscribe: constants.modals.limitedEditionProducts.NeedResubscribe,
                lep_need_subscribe: constants.modals.limitedEditionProducts.NeedSubscribe,
                lep_need_unpause: constants.modals.limitedEditionProducts.NeedUnpause,
                lep_need_unskip: constants.modals.limitedEditionProducts.NeedUnskip,
              })[firstError]

              if (limitedEditionErrorModal) {
                // Last chance for user
                _addLimitedItemsPostponed(failedItems, metadata, { isNotificationVisible })

                actions.models.modals.open(limitedEditionErrorModal, {
                  onCancel: removePostponedLimitedItems,
                })
              }
              else if (firstError === 'lep_need_card_update' && !isWeMissedYouModalOpened) {
                actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateCard)
              }
              else if (firstError === 'lep_need_address_update' && !isSubscriptionUndeliverableModalOpened) {
                actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateAddress)
              }
            }
          }
        }

        return months
      },
      ({ error, message }) => {
        if (!withoutErrorHandler) {
          if (error === 'same_perfume_added') {
            actions.models.modals.open(constants.modals.SameFragranceError)

            filteredItems.forEach((item) => {
              const snowplowMetadata = {
                productId: item.id,
                productName: item.name,
                productBrand: item.brand,
                productCategory: item.category,
              }

              snowplow.track(constants.snowplowEvents.sameProductPopup, snowplowMetadata, false)
            })
          }
          else if (message) {
            actions.models.modals.open(constants.modals.ServerError, {
              content: message,
            })
          }
        }
      }
    )
}

// Save and add item after registration / authorization
const _addItemPostponed = (item, metadata) => {
  if (item.isLimitedEdition) {
    actions.misc.localStorage.setItem(constants.localStorageNames.queueLimitedProduct, { item, metadata })
  }
  else {
    actions.misc.localStorage.setItem(constants.localStorageNames.queueProduct, { item, metadata })
  }

  return Promise.resolve()
}

const _addItemsPostponed = (items, metadata, options) => {
  actions.misc.localStorage.setItem(constants.localStorageNames.queueProducts, { items, metadata, options })
  // window.location.href for reloading page if redirect from SEO page
  actions.models.router.push(links.register, true, true)
}

const _addLimitedItemsPostponed = (items, metadata, options) => {
  actions.misc.localStorage.setItem(constants.localStorageNames.queueLimitedProducts, { items, metadata, options })
}

const addItem = (item, metadata) => {
  const { models: { me: { isLoggedIn, registrationDate } } } = getState()

  let { isLimitedEdition } = item
  // TODO remove this when backend will return isLimitedEdition
  if (typeof isLimitedEdition !== 'boolean' && item.labels) {
    item.isLimitedEdition = item.labels.includes('limited-edition')
  }

  if (isLoggedIn) {
    return _addItem({ item, metadata, registrationDate })
  }

  return _addItemPostponed(item, metadata)
    .then(() => actions.models.router.push(links.register, true, true))
}

const addItems = (items, metadata, isNotificationVisible) => {
  const { models: { me: { isLoggedIn } } } = getState()

  if (isLoggedIn) {
    return _addItems(items, metadata, { isNotificationVisible })
  }

  return _addItemsPostponed(items, metadata, { isNotificationVisible })
}

const removePostponedLimitedItem = () =>
  actions.misc.localStorage.removeItem(constants.localStorageNames.queueLimitedProduct)

const removePostponedLimitedItems = () =>
  actions.misc.localStorage.removeItem(constants.localStorageNames.queueLimitedProducts)

const removeAllPostponedLimitedItems = () => {
  removePostponedLimitedItem()
  removePostponedLimitedItems()
}

/**
 *
 * @param {string} localStorageName
 * @param {Boolean} withoutErrorHandler
 * @param {Boolean} force
 * @returns {Promise.<T>}
 * @private
 */
const _resolvePostponedItem = (localStorageName, withoutErrorHandler, force) => {
  const { item, metadata } = actions.misc.localStorage.getItem(localStorageName, {})

  actions.misc.localStorage.removeItem(localStorageName)

  if (item) {
    return _addItem({ item, metadata, withoutErrorHandler, force })
  }

  return Promise.resolve()
}

const moveCookiesToStorage = () => {
  const cookiesToMove = [
    {
      cookie: constants.cookieNames.postponed.queueProduct,
      storage: constants.localStorageNames.queueProduct,
    },
    {
      cookie: constants.cookieNames.postponed.queueLimitedProduct,
      storage: constants.localStorageNames.queueLimitedProduct,
    },
    {
      cookie: constants.cookieNames.postponed.queueProducts,
      storage: constants.localStorageNames.queueProducts,
    },
    {
      cookie: constants.cookieNames.postponed.queueLimitedProducts,
      storage: constants.localStorageNames.queueLimitedProducts,
    },
  ]

  cookiesToMove.forEach(({ cookie, storage }) => {
    const cookieValue = getCookieValue(cookie)

    if (cookieValue) {
      actions.misc.localStorage.setItem(cookieValue)
      actions.misc.cookie.removeItem(storage)
    }
  })
}

const resolvePostponedItem = () => (
  _resolvePostponedItem(constants.localStorageNames.queueProduct)
)

/**
 *
 * @param {Object} [data]
 * @param {Boolean} [data.withoutErrorHandler] - do not show popups in case of error
 * @param {Boolean} [data.force] - add LEP to queue in case when 1st month is skipped
 */
const resolvePostponedLimitedItem = ({ withoutErrorHandler, force } = {}) => (
  _resolvePostponedItem(constants.localStorageNames.queueLimitedProduct, withoutErrorHandler, force)
)

const resolvePostponedItems = () => {
  const postponedQueueProducts = actions.misc.localStorage.getItem(constants.localStorageNames.queueProducts, {})
  const { items, metadata, options } = postponedQueueProducts

  actions.misc.localStorage.removeItem(constants.localStorageNames.queueProducts)

  if (items && items.length) {
    return _addItems(
      items,
      metadata,
      { ...options, withoutErrorHandler: true, postponeLimitedItems: true }
    )
  }

  return Promise.resolve()
}

/**
 *
 * @param {Object} [data]
 * @param {Boolean} [data.withoutErrorHandler] - do not show popups in case of error
 * @param {Boolean} [data.force] - add LEP to queue in case when 1st month is skipped
 */
const resolvePostponedLimitedItems = ({ withoutErrorHandler, force } = {}) => {
  const postponedQueueProducts = actions.misc.localStorage.getItem(constants.localStorageNames.queueLimitedProducts, {})
  const { items, metadata, options } = postponedQueueProducts

  actions.misc.localStorage.removeItem(constants.localStorageNames.queueLimitedProducts)

  if (items && items.length) {
    return _addItems(
      items,
      metadata,
      { ...options, withoutErrorHandler, force }
    )
  }

  return Promise.resolve()
}

const resolvePickYourScentFlowItems = ({ isNotificationVisible } = {}) => {
  const { view: { pickPage: { selectedItems } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = getFeatureToggle(constants.features.premiumProductsForNotSubscribed)

  if (!isPremiumProductsForNotSubscribedEnabled && selectedItems.length) {
    const pickPageSelectedItems = selectedItems.map((item) => typeof item === 'number' ? item : item.id)
      .filter((id) => Boolean(id))

    actions.view.pickPage.clearSelectedItems()

    return _addItems(
      pickPageSelectedItems,
      { placement: 'pick page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveSkincareFlowItems = ({ isNotificationVisible } = {}) => {
  const { view: { skincareIntroPage: { selectedItems } } } = getState()

  actions.view.skincareIntroPage.clearSelectedItems()

  if (selectedItems.length) {
    const skincareIntroSelectedItems = selectedItems.map((item) => typeof item === 'number' ? item : item.id)
      .filter((id) => Boolean(id))

    return _addItems(
      skincareIntroSelectedItems,
      { placement: 'skincare intro page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveCollectionLandingItems = ({ isNotificationVisible } = {}) => {
  const { view: { collectionLandingPage: { products } } } = getState()

  actions.view.collectionLandingPage.clearProducts()

  const selectedItems = products
    ? products.filter(({ isSelected }) => isSelected).map(({ id }) => id)
    : []

  if (selectedItems.length) {
    return _addItems(
      selectedItems,
      { placement: 'landing collection page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveMakeupLandingItems = ({ isNotificationVisible } = {}) => {
  const { view: { makeupLandingPage: { selectedItems } } } = getState()

  actions.view.makeupLandingPage.clearSelectedItems()

  if (selectedItems && selectedItems.length) {
    return _addItems(
      selectedItems,
      { placement: 'makeup landing page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveMakeupLandingNewStyleItems = ({ isNotificationVisible } = {}) => {
  const { view: { makeupLandingPage: { selectedLandingItems } } } = getState()

  actions.view.makeupLandingPage.clearSelectedLandingItems()

  if (selectedLandingItems.length) {
    const makeupLandingNewStyleSelectedItems = selectedLandingItems.map(({ id }) => id)
      .filter((id) => Boolean(id))

    return _addItems(
      makeupLandingNewStyleSelectedItems,
      { placement: 'main makeup landing pick' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

// Resolving items after login
const resolveAllPostponedItems = async ({ isNotificationVisible } = {}) => {
  try {
    await resolvePostponedItem()
    await resolvePostponedItems()
    await resolvePickYourScentFlowItems({ isNotificationVisible })
    await resolveCollectionLandingItems({ isNotificationVisible })
    await resolveMakeupLandingItems({ isNotificationVisible })
    await resolveMakeupLandingNewStyleItems({ isNotificationVisible })
    await resolveSkincareFlowItems({ isNotificationVisible })
  }
  catch (err) {
    let error = (err && err.stack) ? err.stack : err
    if (typeof error !== 'string') {
      error = JSON.stringify(error)
    }
    sentry.captureException(`resolveAllPostponedItems exception: ${error}`)
  }
}

// Resolving limited items
const resolveAllPostponedLimitedItems = async ({ withoutErrorHandler } = {}) => {
  try {
    await resolvePostponedLimitedItem({ withoutErrorHandler })
    await resolvePostponedLimitedItems({ withoutErrorHandler })
  }
  catch (err) {
    let error = (err && err.stack) ? err.stack : err
    if (typeof error !== 'string') {
      error = JSON.stringify(error)
    }
    sentry.captureException(`resolveAllPostponedLimitedItems exception: ${error}`)
  }
}

const _extendNewMonths = (months, products) => {
  let interval = 0

  return months.map((month) => {
    const productCount = month.products.length
    const productsChunk = products.slice(interval, interval + productCount)

    interval += productCount

    return {
      ...month,
      products: productsChunk,
    }
  })
}

export const moveProduct = (months, oldIndex, newIndex, checkDisabled) => {
  let products = getAllQueueProductsArray(months)

  products = utils.move(products, oldIndex, newIndex, checkDisabled)

  return _extendNewMonths(months, products)
}

/**
 * Remove product by serverIndex
 *
 * @param {Number} months
 * @param {Number} serverIndex
 * @param {Function} checkDisabled
 * @returns {Array} months
 */
export const removeProduct = (months, serverIndex, checkDisabled) => {
  let products = getAllQueueProductsArray(months)
  const itemIndex = products.findIndex(({ index }) => index === serverIndex)

  products[itemIndex] = { isEmpty: true, disabled: true }

  products = utils.remove(products, itemIndex, checkDisabled)

  cookie.remove(constants.cookieNames.buyQueuePageData)

  return _extendNewMonths(months, products)
}

/**
 *
 * @param {Object} params
 * @param {number} params.oldIndex
 * @param {number} params.newIndex
 * @param {Object} params.metadata
 * @param {string} params.metadata.placement
 * @param {number} params.metadata.id
 * @param {string} params.metadata.name
 * @param {string} params.metadata.brand
 */
const moveItem = ({ oldIndex, newIndex, metadata }) => {
  if (oldIndex === newIndex) {
    return
  }

  const { models: { queue: { items: months }, subscription: { currPlan: { shippingPeriod } } } } = getState()
  const { id, index: oldServerIndex, isLimitedEdition, isUnavailable: wasUnavailable } = getItemByIndex(oldIndex)
  const { index: newServerIndex } = getItemByIndex(newIndex)

  // get 2nd month bcs 1st month may be locked with not actual plan size
  const planSize = months[1].status.length
  const newItems = moveProduct(months, oldIndex, newIndex, ({ disabled }) => disabled)

  const data = {
    item: id,
    indexFrom: oldServerIndex,
    indexTo: newServerIndex,
    page: metadata.placement,
  }

  const snowplowMetadata = {
    indexFrom: oldServerIndex,
    indexTo: newServerIndex,
    productName: metadata.name,
    productBrand: metadata.brand,
    productId: metadata.id,
  }

  reducers.models.queue.setItems(newItems)

  snowplow.track(constants.snowplowEvents.queueProductMove, snowplowMetadata, false)

  return request.put('rest/subscription/queue/items', {
    body: data,
    modifyResult: modifyItems,
    withLoader: true,
    withErrorModal: false,
  })
    .then(({ items: months }) => {
      reducers.models.queue.setItems(months)

      const item = getItemByServerIndex(newServerIndex, months)
      const { isUnavailable } = item

      const isLEPMovedBackward = (
        isLimitedEdition
        && oldServerIndex < planSize
        && newServerIndex >= planSize
        && (!months[0].disabled || !months[0].skipped && !months[1].disabled)
      )
      const isPutFirstInQueueModalVisible = (
        isLEPMovedBackward
        && !wasUnavailable
        && !isUnavailable
        && !getCookieValue(constants.cookieNames.isPutFirstInQueueModalShown)
        && shippingPeriod === 1
      )

      if (isPutFirstInQueueModalVisible) {
        actions.models.modals.open(
          constants.modals.limitedEditionProducts.PutFirstInQueue,
          { productIndex: newServerIndex, item },
        )
        actions.misc.cookies.setSessionItem(constants.cookieNames.isPutFirstInQueueModalShown, true)
      }
    }, ({ error, title, message, resData: { items } }) => {
      const { items: months } = modifyItems({ items })

      reducers.models.queue.setItems(months)

      const limitedEditionErrorModals = {
        lep_need_change_frequency: constants.modals.limitedEditionProducts.NeedChangeFrequency,
        lep_need_resubscribe: constants.modals.limitedEditionProducts.NeedResubscribe,
        lep_need_subscribe: constants.modals.limitedEditionProducts.NeedSubscribe,
        lep_need_unpause: constants.modals.limitedEditionProducts.NeedUnpause,
        lep_need_unskip: constants.modals.limitedEditionProducts.NeedUnskip,
        lep_need_card_update: constants.modals.limitedEditionProducts.NeedUpdateCard,
        lep_need_address_update: constants.modals.limitedEditionProducts.NeedUpdateAddress,
      }

      if (Object.keys(limitedEditionErrorModals).includes(error)) {
        actions.models.modals.open(limitedEditionErrorModals[error], { isMoveAction: true })
      }
      else if (error === 'lep_impossible_put_first_in_queue') {
        actions.models.modals.open(constants.modals.limitedEditionProducts.OutOfStock, { cantMoveInQueue: true })
      }
      else if (message) {
        actions.models.modals.open(constants.modals.ServerError, {
          title,
          content: message,
        })
      }
      else {
        actions.models.modals.open(constants.modals.SomethingWentWrong)
      }
    })
}

const moveToFirstPosition = (serverIndex, metadata) => {
  const { models: { queue: { items: months } } } = getState()

  const allProducts = getAllQueueProductsArray(months)
  const oldIndex = allProducts.findIndex((item) => item && (item.index === serverIndex))
  const newIndex = allProducts.findIndex((item) => item && (item.index === 0))

  if (oldIndex === -1 || newIndex === -1) {
    sentry.captureException(`moveToFirstPosition: items not found`, {
      extra: {
        serverIndex,
        metadata,
        oldIndex,
        newIndex,
        allProducts,
        months,
      },
    })
    return
  }

  moveItem({ oldIndex, newIndex, metadata })
}

/**
 *
 * @param {Object} params
 * @param {number} params.id
 * @param {number} params.index - serverIndex!
 * @param {string} params.page
 * @param {Object} params.metadata
 * @param {string} params.metadata.placement
 * @param {string} params.metadata.name
 * @param {string} params.metadata.brand
 */
const removeItem = ({ id: itemId, index: serverIndex, page, metadata }) => {
  const { models: { queue: { items: months } } } = getState()

  const newItems = removeProduct(months, serverIndex, ({ disabled }) => disabled)

  const data = {
    page,
    metadata,
    item: itemId,
    index: serverIndex,
  }

  const snowplowMetadata = {
    placement: metadata.placement,
    productName: metadata.name,
    productBrand: metadata.brand,
    productId: itemId,
  }

  reducers.models.queue.setItems(newItems)
  reducers.models.me.decreaseStatsQueueCount()

  snowplow.track(constants.snowplowEvents.queueProductRemove, snowplowMetadata, false)

  return request.post('rest/subscription/queue/items/delete', {
    body: data,
    modifyResult: modifyItems,
    withLoader: true,
  })
    .then(({ items: months }) => {
      reducers.models.queue.setItems(months)
    }, ({ resData: { items } }) => {
      const { items: months } = modifyItems({ items })

      reducers.models.queue.setItems(months)
      reducers.models.me.increaseStatsQueueCount()
      actions.models.modals.open(constants.modals.SomethingWentWrong)
    })
}

const clear = () =>
  request.delete('rest/subscription/queue')


export default {
  fetch,
  addItem,
  addItems,
  resolveAllPostponedItems,
  resolveAllPostponedLimitedItems,
  removeAllPostponedLimitedItems,
  moveItem,
  moveToFirstPosition,
  removeItem,
  clear,
  moveCookiesToStorage,
}

export {
  modifyItems,
}
